import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { CORRIDORS } from "@/lib/constants";
import { toast } from "sonner";

export default function AdminSettings() {
  const [activeCorridor, setActiveCorridor] = useState("tg_ga");
  const [byCorridor, setByCorridor] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setByCorridor(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const current = byCorridor[activeCorridor] || {};
  const setField = (k, v) => {
    setByCorridor((p) => ({
      ...p,
      [activeCorridor]: { ...(p[activeCorridor] || {}), [k]: v },
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        corridor: activeCorridor,
        exchange_rate: parseFloat(current.exchange_rate),
        fee_percent: parseFloat(current.fee_percent),
        fee_fixed: parseFloat(current.fee_fixed),
      };
      if (current.min_amount !== "" && current.min_amount !== undefined && current.min_amount !== null) {
        body.min_amount = parseFloat(current.min_amount);
      }
      if (activeCorridor === "ga_fr" || activeCorridor === "ga_rw") {
        if (current.referral_discount_percent !== "" && current.referral_discount_percent !== undefined && current.referral_discount_percent !== null) {
          body.referral_discount_percent = parseFloat(current.referral_discount_percent);
        }
        if (current.referral_commission_min_xof !== "" && current.referral_commission_min_xof !== undefined && current.referral_commission_min_xof !== null) {
          body.referral_commission_min_xof = parseFloat(current.referral_commission_min_xof);
        }
      }
      if (activeCorridor === "fr_ga") {
        if (current.paypal_email !== undefined && current.paypal_email !== null) body.paypal_email = current.paypal_email;
        if (current.wero_number !== undefined && current.wero_number !== null) body.wero_number = current.wero_number;
        if (current.wero_name !== undefined && current.wero_name !== null) body.wero_name = current.wero_name;
      }
      const { data } = await api.put("/admin/settings", body);
      setByCorridor((p) => ({ ...p, [activeCorridor]: data }));
      toast.success(`Paramètres ${CORRIDORS[activeCorridor].short} enregistrés`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (loading) return <div>Chargement…</div>;
  const c = CORRIDORS[activeCorridor];

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold">Paramètres</h1>
      <p className="text-gray-600 mt-1">Configurez le taux de change et les frais par corridor.</p>

      <ReferralConfigCard />

      <div className="mt-6 inline-flex p-1 bg-white rounded-full border border-gray-200" data-testid="settings-corridor-switch">
        {Object.values(CORRIDORS).map((co) => (
          <button
            key={co.id}
            type="button"
            onClick={() => setActiveCorridor(co.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              activeCorridor === co.id ? "bg-[var(--sl-primary)] text-white" : "text-gray-700 hover:text-[var(--sl-primary)]"
            }`}
            data-testid={`settings-tab-${co.id}`}
          >
            {co.flagFrom} {co.short} {co.flagTo}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="sl-card mt-6 max-w-2xl space-y-5" data-testid="settings-form">
        <div className="text-xs uppercase tracking-widest text-gray-500">
          Corridor actif : {c.label}
        </div>
        <div>
          <label className="sl-label">Taux de change {c.source_currency} → {c.target_currency}</label>
          <input
            type="number" step="any" min="0"
            className="sl-input" required
            value={current.exchange_rate ?? ""}
            onChange={(e) => setField("exchange_rate", e.target.value)}
            data-testid="settings-rate"
          />
          <p className="text-xs text-gray-500 mt-1">
            Combien de {c.target_currency} reçoit le bénéficiaire pour 1 {c.source_currency}.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="sl-label">Frais en pourcentage (%)</label>
            <input
              type="number" step="0.01" min="0"
              className="sl-input" required
              value={current.fee_percent ?? ""}
              onChange={(e) => setField("fee_percent", e.target.value)}
              data-testid="settings-fee-percent"
            />
          </div>
          <div>
            <label className="sl-label">Frais fixes ({c.source_currency})</label>
            <input
              type="number" step="1" min="0"
              className="sl-input" required
              value={current.fee_fixed ?? ""}
              onChange={(e) => setField("fee_fixed", e.target.value)}
              data-testid="settings-fee-fixed"
            />
          </div>
        </div>
        <div>
          <label className="sl-label">Montant minimum de transaction ({c.source_currency})</label>
          <input
            type="number" step="1" min="0"
            className="sl-input"
            value={current.min_amount ?? ""}
            placeholder={c.source_currency === "EUR" ? "20" : "2000"}
            onChange={(e) => setField("min_amount", e.target.value)}
            data-testid="settings-min-amount"
          />
          <p className="text-xs text-gray-500 mt-1">
            Laisser vide pour utiliser la valeur par défaut ({c.source_currency === "EUR" ? "20 €" : "2 000 " + c.source_currency}).
          </p>
        </div>

        {(activeCorridor === "ga_fr" || activeCorridor === "ga_rw") && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 space-y-4" data-testid="referral-settings-block">
            <div className="text-xs uppercase tracking-widest text-amber-700 font-semibold">Programme de parrainage — {c.label}</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="sl-label">Remise filleul (%)</label>
                <input
                  type="number" step="0.5" min="0" max="100"
                  className="sl-input"
                  value={current.referral_discount_percent ?? ""}
                  placeholder={activeCorridor === "ga_fr" ? "15" : "10"}
                  onChange={(e) => setField("referral_discount_percent", e.target.value)}
                  data-testid="settings-referral-discount"
                />
                <p className="text-xs text-gray-500 mt-1">Pourcentage de réduction appliqué aux frais.</p>
              </div>
              <div>
                <label className="sl-label">Seuil commission 5 € ({c.source_currency})</label>
                <input
                  type="number" step="1000" min="0"
                  className="sl-input"
                  value={current.referral_commission_min_xof ?? ""}
                  placeholder={activeCorridor === "ga_fr" ? "200000" : "100000"}
                  onChange={(e) => setField("referral_commission_min_xof", e.target.value)}
                  data-testid="settings-referral-min"
                />
                <p className="text-xs text-gray-500 mt-1">Montant minimum à partir duquel le parrain gagne 5 €.</p>
              </div>
            </div>
          </div>
        )}

        {activeCorridor === "fr_ga" && (
          <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4 space-y-4" data-testid="frga-accounts-block">
            <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">Comptes de dépôt France → Gabon</div>
            <div>
              <label className="sl-label">Email PayPal (Entre proches)</label>
              <input
                type="email"
                className="sl-input"
                value={current.paypal_email ?? ""}
                placeholder="exemple@gmail.com"
                onChange={(e) => setField("paypal_email", e.target.value)}
                data-testid="settings-paypal-email"
              />
              <p className="text-xs text-gray-500 mt-1">Adresse PayPal affichée au client pour le paiement.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="sl-label">Numéro Wero</label>
                <input
                  type="text"
                  className="sl-input"
                  value={current.wero_number ?? ""}
                  placeholder="+33 7 49 57 44 48"
                  onChange={(e) => setField("wero_number", e.target.value)}
                  data-testid="settings-wero-number"
                />
              </div>
              <div>
                <label className="sl-label">Nom du compte Wero</label>
                <input
                  type="text"
                  className="sl-input"
                  value={current.wero_name ?? ""}
                  placeholder="MOULEMA Cerny SOKI"
                  onChange={(e) => setField("wero_name", e.target.value)}
                  data-testid="settings-wero-name"
                />
              </div>
            </div>
          </div>
        )}

        <button type="submit" disabled={saving} className="sl-btn-primary" data-testid="settings-save">
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </form>
    </div>
  );
}

function ReferralConfigCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/referral-config").then(({ data }) => setCfg(data)).catch(() => setCfg({}));
  }, []);

  if (cfg === null) return null;

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        discount_percent: parseFloat(cfg.discount_percent),
        tier_threshold_xaf: parseFloat(cfg.tier_threshold_xaf),
        tier_commission_eur: parseFloat(cfg.tier_commission_eur),
        code_reusable: !!cfg.code_reusable,
      };
      const { data } = await api.patch("/admin/referral-config", payload);
      setCfg(data);
      toast.success("Programme de parrainage mis à jour");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-6 sl-card border-2 border-amber-200 bg-amber-50/40" data-testid="referral-config-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-700 font-semibold">Programme de parrainage</div>
          <h2 className="font-heading text-lg font-bold mt-0.5">Configuration globale</h2>
          <p className="text-xs text-gray-500 mt-1">Ces paramètres s&apos;appliquent aux corridors Gabon → France et Gabon → Rwanda.</p>
        </div>
      </div>

      <div className="mt-5 grid md:grid-cols-2 gap-4">
        <div>
          <label className="sl-label">Remise filleul (%)</label>
          <input
            type="number" step="0.5" min="0" max="100"
            className="sl-input"
            value={cfg.discount_percent ?? ""}
            onChange={(e) => set("discount_percent", e.target.value)}
            data-testid="referral-cfg-discount"
          />
          <p className="text-xs text-gray-500 mt-1">Réduction appliquée aux frais quand le code est utilisé.</p>
        </div>
        <div>
          <label className="sl-label">Seuil de cumul (FCFA)</label>
          <input
            type="number" step="10000" min="1000"
            className="sl-input"
            value={cfg.tier_threshold_xaf ?? ""}
            onChange={(e) => set("tier_threshold_xaf", e.target.value)}
            data-testid="referral-cfg-threshold"
          />
          <p className="text-xs text-gray-500 mt-1">Volume cumulé des transferts des filleuls qui déclenche une récompense.</p>
        </div>
        <div>
          <label className="sl-label">Récompense par palier (€)</label>
          <input
            type="number" step="0.5" min="0"
            className="sl-input"
            value={cfg.tier_commission_eur ?? ""}
            onChange={(e) => set("tier_commission_eur", e.target.value)}
            data-testid="referral-cfg-commission"
          />
          <p className="text-xs text-gray-500 mt-1">Montant crédité au parrain à chaque palier atteint.</p>
        </div>
        <div>
          <label className="sl-label">Codes réutilisables</label>
          <div className="flex items-center gap-3 mt-1">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!cfg.code_reusable}
                onChange={(e) => set("code_reusable", e.target.checked)}
                data-testid="referral-cfg-reusable"
              />
              <span className="text-sm">{cfg.code_reusable ? "Oui — chaque code peut servir à plusieurs filleuls" : "Non — un code est consommé après usage"}</span>
            </label>
          </div>
        </div>
      </div>

      <div className="mt-5">
        <button type="button" onClick={save} disabled={saving} className="sl-btn-primary text-sm" data-testid="referral-cfg-save">
          {saving ? "Enregistrement…" : "Enregistrer le programme"}
        </button>
      </div>
    </div>
  );
}
